import React, { useState, useEffect } from "react";
import { Select, Button, Modal, Empty, Pagination } from "antd";
import { RightOutlined, FilterFilled, FilterOutlined } from "@ant-design/icons";
import { useHistory } from "react-router-dom";
import "./MobileTable.css";
import { getPodIdAndPodNameMapping } from "src/utils/getFilterOptions";
import { safeGet, truncate } from "src/shared/utils";
import { fetchRestaurantData } from "src/utils/restaurantUtils";

const { Option } = Select;

const MobileTable = ({ data, allowGoingNextPage = true, userRolesPayload }) => {
  const [filterStoreIds, setFilterStoreIds] = useState([]);
  const [selectedPod, setSelectedPod] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [restaurantData, setRestaurantData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const history = useHistory();

  useEffect(() => {
    const getRestaurantData = async () => {
      const data = await fetchRestaurantData();
      setRestaurantData(data);
    };

    getRestaurantData();
  }, []);
  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterStoreIds]);

  // Create the restaurant name map after data is loaded
  const restaurantNameMap = {};
  if (restaurantData && restaurantData.length > 0) {
    restaurantData.forEach((restaurant) => {
      if (restaurant && restaurant.restaurant_id) {
        // Store with both string and number keys to handle type mismatches
        const id = restaurant.restaurant_id;
        restaurantNameMap[id] = restaurant.restaurant_name;
        restaurantNameMap[String(id)] = restaurant.restaurant_name;
        restaurantNameMap[Number(id)] = restaurant.restaurant_name;
      }
    });
  }

  const handleArrowClick = (restaurantId) => {
    history.push(`/india-2-app/third-screen?restaurant_id=${restaurantId}`);
  };

  // Helper function to get restaurant name handling potential type mismatches
  const getRestaurantName = (id) => {
    if (!id) return "";
    // Try various formats of the ID
    return restaurantNameMap[id] ||
      restaurantNameMap[String(id)] ||
      restaurantNameMap[Number(id)] ||
      "";
  };

  if (!data?.payload?.records?.length) {
    return (
      <div
        className="pod-section"
        style={{
          display: "flex",
          justifyContent: "center",
          padding: "2rem",
          margin: "1rem",
          backgroundColor: "#fff",
          borderRadius: "8px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        }}
      >
        <Empty
          description={<span style={{ fontSize: "16px", color: "#595959" }}>No data available</span>}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </div>
    );
  }

  const podMapping = getPodIdAndPodNameMapping(userRolesPayload);

  const restaurantOptions = [...new Set(data.payload.records.map((record) => record.restaurant_id))].map(
    (restaurantId) => {
      const restaurantName = getRestaurantName(restaurantId);
      return {
        value: restaurantId.toString(),
        label: `${restaurantId} - ${restaurantName || restaurantId}`,
      };
    }
  );

  // First apply filtering - this maintains the existing filter functionality
  const filteredRecords = data.payload.records.filter((record) => {
    return filterStoreIds.length ? filterStoreIds.includes(record.restaurant_id.toString()) : true;
  });

  // Then apply pagination to the filtered results
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, filteredRecords.length);
  const currentPageData = filteredRecords.slice(startIndex, endIndex);

  const handleApplyFilters = () => {
    setIsModalVisible(false);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    // Scroll to top when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePageSizeChange = (value) => {
    setPageSize(value);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  // Generate page options for the dropdown
  const totalPages = Math.ceil(filteredRecords.length / pageSize);
  const pageOptions = Array.from({ length: totalPages }, (_, i) => ({
    value: i + 1,
    label: `${i + 1}`
  }));

  const pageSizeOptions = [
    { value: 10, label: "10 records per page" },
    { value: 20, label: "20 records per page" },
    { value: 50, label: "50 records per page" },
    { value: 100, label: "100 records per page" }
  ];

  const isFilterActive = selectedPod.length > 0;

  return (
    <div className="mobile-table">
      <div style={{ marginLeft: "1rem", color: "black", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        {isFilterActive ? (
          <FilterFilled style={{ fontSize: "2rem", color: "blue" }} onClick={() => setIsModalVisible(true)} />
        ) : (
          <FilterOutlined style={{ fontSize: "2rem" }} onClick={() => setIsModalVisible(true)} />
        )}
      </div>

      {/* Pagination Controls at Top */}
      {filteredRecords.length > 0 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', margin: '10px 0 20px', padding: '5px 10px', backgroundColor: '#f0f0f0', borderRadius: '5px' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span style={{ marginRight: '10px' }}>Page</span>
            <Select
              showSearch
              style={{ width: 60, marginRight: '15px', border: '0.5px solid black' }}
              value={currentPage}
              onChange={handlePageChange}
              options={pageOptions}
              filterOption={(input, option) =>
                option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span style={{ marginRight: '10px' }}>Show</span>
            <Select
              style={{ width: 160, border: '0.5px solid black' }}
              value={pageSize}
              onChange={handlePageSizeChange}
              options={pageSizeOptions}
            />
          </div>
        </div>
      )}

      <Modal
        title="Filter Options"
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        maskStyle={{
          backgroundColor: "rgba(0, 0, 0, 0.9)",
        }}
        footer={[
          <Button
            key="clear"
            onClick={() => {
              setSelectedPod([]);
              setFilterStoreIds([]);
              setIsModalVisible(false);
            }}
          >
            Clear Filters
          </Button>,
          <Button key="ok" type="primary" onClick={handleApplyFilters}>
            OK
          </Button>,
        ]}
      >
        <Select
          id="podFilter"
          mode="multiple"
          value={selectedPod}
          onChange={(value) => {
            setFilterStoreIds(value);
            setSelectedPod(value);
          }}
          placeholder="Select a Restaurant"
          showSearch
          allowClear
          filterOption={(input, option) => {
            const label = option?.label;
            return typeof label === "string" && label.toLowerCase().includes(input.toLowerCase());
          }}
          options={restaurantOptions}
          style={{ width: "100%", border: '0.5px solid black' }}
          dropdownStyle={{ maxHeight: "200px", overflow: "auto" }}
        />
      </Modal>

      <div className="filter-section sticky" style={{ marginBottom: "10px" }}>
        <Select
          mode="multiple"
          allowClear
          style={{ width: "100%", maxHeight: "200px", overflowY: "auto", display: "none" }}
          placeholder="Select store IDs"
          value={filterStoreIds}
          onChange={(value) => setFilterStoreIds(value)}
        >
          {filteredRecords.map((record) => (
            <Option key={record.restaurant_id} value={record.restaurant_id.toString()}>
              {record.restaurant_id}
            </Option>
          ))}
        </Select>
      </div>

      {filteredRecords.length === 0 ? (
        <p>No matching records found</p>
      ) : (
        <>
          {currentPageData.map((record, index) => {
            const restaurantName = getRestaurantName(record.restaurant_id);

            return (
              <div className="pod-section" key={index}>
                <div className="pod-header">
                  <div>
                    <p className="pod-name">
                      {record?.restaurant_id || "N/A"}
                      {restaurantName ? ` - ${restaurantName}` : ""}
                    </p>
                    <p style={{ fontSize: "1rem" }}>
                      {record?.restaurant_id
                        ? truncate(safeGet(podMapping, record.restaurant_id.toString()) || "", 35)
                        : ""}
                    </p>
                  </div>
                  {allowGoingNextPage && record?.restaurant_id && (
                    <div
                      className="arrow"
                      onClick={() => handleArrowClick(record.restaurant_id)}
                      style={{
                        cursor: "pointer",
                        backgroundColor: "#f0f0f0",
                        padding: "0.7rem",
                        borderRadius: "5px",
                        fontWeight: "bold",
                      }}
                    >
                      <RightOutlined />
                    </div>
                  )}
                </div>

                <div className="details">
                  {Object.entries(record).map(([key, value]) => {
                    if (key === "metric_breakdown") {
                      try {
                        const parsedMetricBreakdown = typeof value === "string" ? JSON.parse(value) : value;

                        return (
                          <div key={key}>
                            <MetricTable
                              data={parsedMetricBreakdown}
                              restaurantId={record.restaurant_id}
                              restaurantName={restaurantName}
                            />
                          </div>
                        );
                      } catch (error) {
                        console.error("Error parsing metric_breakdown:", error);
                        return (
                          <p key={key}>
                            <strong>{key.replace(/_/g, " ")}:</strong> Invalid JSON
                          </p>
                        );
                      }
                    }
                  })}
                </div>
              </div>
            );
          })}

          {/* Bottom Pagination component */}
          <div style={{ display: 'flex', justifyContent: 'center', margin: '20px 0' }}>
            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={filteredRecords.length}
              onChange={handlePageChange}
              onShowSizeChange={(current, size) => handlePageSizeChange(size)}
              showSizeChanger={true}
              pageSizeOptions={['10', '20', '50', '100']}
              showQuickJumper={filteredRecords.length > 50}
            />
          </div>
        </>
      )}
    </div>
  );
};

const MetricTable = ({ data, restaurantId, restaurantName }) => {
  if (!data || Object.keys(data).length === 0) {
    return <p>No metric data available</p>;
  }

  // Get the first metric name (key) that isn't "hideme". We are hiding the rows with key "hideme"
  const metricName = Object.keys(data).find((key) => key !== "hideme") || Object.keys(data)[0];

  const metricData = data[metricName];

  const allowedColumns = [
    "today",
    "yesterday",
    "w1",
    "w2",
    "w3",
    "w4",
    "sdlw",
    "name",
    "time_bucket",
    "item_name",
    "PER_MENU_VISITS_WITH_ITEM_OOS",
    "%_MENU_VISITS_WITH_ITEM_OOS",
    "menu_visits_item_oos",
    "menu_visits",
    "menu_visits_item_oos_percentage",
    "menu_visits_percentage",
    "HS APPLIED SOURCE",
    "START TIME",
    "END TIME",
    "HS Duration (hrs)",
    "today vs yesterday",
    "today vs SDLW",
    "today vs SDLLW",
    "Reason",
    "# Instances",
  ].filter((key) => Object.prototype.hasOwnProperty.call(metricData, key));

  const formatValue = (value) => {
    if (value === null || value === undefined || value === "NA") {
      return "NA";
    } else if (typeof value === "number") {
      return Number(value).toFixed(2);
    } else {
      return value.toString();
    }
  };

  return (
    <div style={{ overflowX: "auto", borderRadius: "5px" }}>
      <table style={{ borderCollapse: "collapse", width: "100%" }}>
        <thead style={{ backgroundColor: "lightgrey" }}>
          <tr>
            <th style={cellStyle}>Metric</th>
            {allowedColumns.map((column) => (
              <th key={column} style={cellStyle}>
                {column.toUpperCase()}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {/* Skip rendering the main metric row if metricName is "hideme" */}
          {metricName !== "hideme" && (
            <tr>
              <td style={{ ...cellStyle, fontWeight: "bold" }}>{metricName.replace(/_/g, " ").toUpperCase()}</td>
              {allowedColumns.map((column) => (
                <td key={column} style={cellStyle}>
                  {formatValue(metricData[column])}
                </td>
              ))}
            </tr>
          )}

          {metricData.breakdown &&
            Object.entries(metricData.breakdown)
              .filter(([breakdownKey]) => breakdownKey !== "hideme") // Filter out breakdown items with key "hideme"
              .map(([breakdownKey, breakdownValues]) => (
                <tr key={breakdownKey}>
                  <td style={cellStyle}>{breakdownKey.replace(/_/g, " ").toUpperCase()}</td>
                  {allowedColumns.map((column) => (
                    <td key={column} style={cellStyle}>
                      {formatValue(breakdownValues[column])}
                    </td>
                  ))}
                </tr>
              ))}
        </tbody>
      </table>
    </div>
  );
};

const cellStyle = {
  border: "1px solid #ddd",
  padding: "8px",
  textAlign: "center",
};

export default MobileTable;
